import { CoreStoreInstance } from "../Stores";

interface EventBusOptions {
    strict: boolean;
    log: boolean;
}
interface Flags {
    once: boolean;
    recoup: boolean;
}
export class PubSub {
    private options: EventBusOptions;
    private pubSubEvents: any = {};
    private pubSubHappened: any = {};

    constructor() {
        this.options = {
            /*
				Enables strict mode (shows more warnings)
			 */
            strict: false,

            /*
				Accepts a custom logger
			 */
            log: false,

            //...options,
        } as EventBusOptions;
        this.pubSubEvents = {};
        this.pubSubHappened = {};
    }

    /*
		Publish an event.
		
		* **pub(event, [arg1, [arg2 ...]])**
		 * *event* the event to trigger;
		 * *arg1 ... argN* (optional) any number of additional params to pass to the event subscribers.
	*/
    pub(eventString: string, payload: any) {
        if (!eventString) {
            throw new Error("pubsub.pub() received empty event string");
        }

        if (this.options.strict && arguments.length > 2) {
            console.warn(`[Deprecated] pub(): Publishing an event with many arguments is deprecated; use a single argument for the payload. Re: ${eventString}`);
        }

        let eventComponents = this.eventNamespace(eventString);
        let eventArray, i, ilen, j, jlen, args, subscriber, ret, event;
        for (i = 0, ilen = eventComponents.length; i < ilen; i++) {
            eventArray = this.pubSubEvents[(event = eventComponents[i])] || [];

            CoreStoreInstance.coreLogger.logInformation("pub", event, args);

            for (j = 0, jlen = eventArray.length; j < jlen; j++) {
                subscriber = eventArray[j];
                //ret = subscriber[0].apply(subscriber[1] || this, payload);
                ret = subscriber[0](payload);
                if (subscriber[2] && ret !== false) {
                    this.unsub(event, subscriber[0]);
                }
            }
            this.pubSubHappened[event] = args;
        }
        return this;
    }

    /*
		Subscribe a function to one or more events.
		
		 * **sub(eventString, method, [thisArg, [flags]])**
		   * *eventString* one or more space-separated events;
		   * *method* the function to subscribe to the events;
		   * *thisArg* (optional) context for the method;
		   * *flags* (optional) boleans to configure the subscribers's behavior:
		     * *once* if true, the subscriber will self-unsubscribe after the first successful execution. Use *pubsub.once()* for clarity;
		     * *recoup* if true, the subscriber will execute immediately if the event it subscribes to already happened. Use *pubsub.recoup()* for clarity.
		
		*Note:* When subscribing to multiple events, make sure these events are published with the same (or similar) list of arguments.
	*/
    sub(eventStr: string, method: any, thisArg?: any, flags?: Flags) {
        if (!eventStr) {
            throw new Error("sub(): received empty event string");
        }

        if (!method) {
            throw new Error("sub(): listener is undefined");
        }

        if (this.options.strict && thisArg) {
            CoreStoreInstance.coreLogger.logWarning(`[Deprecated] sub(): thisArg is deprecated; bind method manually instead. Re: ${eventStr}`);
        }

        CoreStoreInstance.coreLogger.logInformation("sub", eventStr, method, flags);

        let events;

        if (Array.isArray(eventStr)) {
            events = eventStr;
        } else {
            events = eventStr.split(/\s+/);
            if (this.options.strict && events.length > 1) {
                CoreStoreInstance.coreLogger.logWarning(`[Deprecated] sub(): Subscribing to space-separated events is deprecated; use Array instead. Re: ${eventStr}`);
            }
        }

        let event, eventArray, i, len, oldArgs;

        flags = flags || { once: false, recoup: false };
        for (i = 0, len = events.length; i < len; i++) {
            eventArray = this.pubSubEvents[(event = events[i])];
            if (eventArray) {
                eventArray.push([method, thisArg, flags.once]);
            } else {
                this.pubSubEvents[event] = [[method, thisArg, flags.once]];
            }

            if (this.options.strict && this.pubSubHappened[event]) {
                CoreStoreInstance.coreLogger.logWarning(`[Warn] Subscribing to an event that already happened. Re: ${eventStr}`);
            }

            if (flags.recoup) {
                if (this.options.strict) {
                    CoreStoreInstance.coreLogger.logInformation(`[Deprecated] recoup(): this method is deprecated; please consider an alternative pattern. Re: ${eventStr}`);
                }

                oldArgs = this.pubSubHappened[event];
                if (oldArgs) {
                    method.apply(thisArg || this, oldArgs);
                }
            }
        }
        return this;
    }

    /*
		Unsubscribe a function from one or more events.
		
		 * **unsub(eventString, method)**
		   * *eventString* one or more space-separated events;
		   * *method* (optional) the function to unsubscribe
		
		If no *method* is provided, all attached methods will be unsubscribed from the event(s).
	*/
    unsub(eventStr: string, method: any) {
        if (!eventStr) {
            throw new Error("pubsub.unsub() received empty event string");
        }

        if (this.options.strict && !method) {
            CoreStoreInstance.coreLogger.logInformation(`[Deprecated] unsub(): called without a listener. Re: ${eventStr}`);
        }

        CoreStoreInstance.coreLogger.logInformation("unsub", eventStr, method);

        let events;

        if (Array.isArray(eventStr)) {
            events = eventStr;
        } else {
            events = eventStr.split(/\s+/);
            if (this.options.strict && events.length > 1) {
                CoreStoreInstance.coreLogger.logInformation(`[Deprecated] unsub(): Unsubscribing to space-separated events is deprecated; use Array instead. Re: ${eventStr}`);
            }
        }

        let event, eventArray, newEventArray, i, j;
        for (i = 0; i < events.length; i++) {
            eventArray = this.pubSubEvents[(event = events[i])] || [];
            newEventArray = [];
            for (j = 0; method && j < eventArray.length; j++) {
                if (eventArray[j][0] !== method) {
                    newEventArray.push(eventArray[j]);
                }
            }
            this.pubSubEvents[event] = newEventArray;
        }
        return this;
    }

    /*
		### pubsub.once
		Subscribe to an event once.
		
		The function will be unsubscribed upon successful exectution.
		To mark the function execution as unsuccessful
		(and thus keep it subscribed), make it return `false`.
		
		 * **once(eventString, method, thisArg)** identical to *pubsub.sub()*.
	*/
    once(eventStr: string, method: any, thisArg: any) {
        return this.sub(eventStr, method, thisArg, { once: true, recoup: false } as Flags);
    }

    /*
		### pubsub.recoup
		Subscribe to an event, and execute immediately if that event was ever published before.
		
		If executed immediately, the subscriber will get as parameters the last values sent with the event.
		
		 * **recoup(eventString, method, thisArg)** identical to *pubsub.sub()*.
	*/
    recoup(eventStr: string, method: any, thisArg: any) {
        return this.sub(eventStr, method, thisArg, { once: false, recoup: true } as Flags);
    }

    /*
		### pubsub.eventNamespace
		Parses the namespaced event string and returns an array of events to publish.
		The original implementation does: `"path:to:event" => ["path", "path:to", "path:to:event"]`.
		Overwrite the implementation to create your custom namespacing rules.
	*/
    eventNamespace(eventString: string) {
        let events = [],
            str = "",
            ch,
            i;
        for (i = 0; i < eventString.length; i++) {
            if ((ch = eventString.charAt(i)) === ":") {
                events.push(str);
            }
            str += ch;
        }
        events.push(str);
        return events;
    }
}
